import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { getOrgRole, isEndUser } from './roles';

type EndUserSelfGuardProps = {
  children: React.ReactNode;
};

/**
 * FLEXO-6068: data-isolation guard for end-user member routes.
 *
 * An end user may only view their OWN member aggregate (`/member/:id` where
 * `:id` is their own `flexo_user_id`). Any attempt to open another member's id
 * via direct URL is redirected back to their own member route. Staff
 * (admin/supervisor) are unaffected and can open any member id.
 *
 * The owning member id is derived from the authenticated user's metadata, never
 * from route input. This is defence-in-depth on top of backend JWT scoping.
 */
export function EndUserSelfGuard({ children }: EndUserSelfGuardProps) {
  const user = useSelector((state: any) => state.user);
  const { id } = useParams();

  const role = getOrgRole(user);
  const ownMemberId =
    user?.metadata?.flexo_user_id || user?.tokenContent?.flexo_user_id;

  if (isEndUser(role) && ownMemberId && id && id !== ownMemberId) {
    return <Navigate to={`/member/${ownMemberId}`} replace />;
  }

  return <>{children}</>;
}
